// ============================================================
//  src/services/api.js
//  All API calls to the NGC Tracker backend.
//  Change API_BASE_URL to point to Railway or localhost.
// ============================================================

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Auth token injection
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ngc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ngc_token');
      localStorage.removeItem('ngc_worker');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ---- Auth (public — no token required) ----
export const searchWorkersForLogin = (name) => api.post('/auth/search', { name });
// withCredentials: lets the server set / read the shared NGC sign-in cookie
// (only active on *.nxtgc.com addresses; harmless everywhere else).
export const loginAsWorker = (worker_id, employee_id) => api.post('/auth/login', { worker_id, employee_id }, { withCredentials: true });
export const checkSharedSignIn = () => axios.get(`${API_BASE_URL}/api/v1/auth/sso`, { withCredentials: true, timeout: 8000 });
export const sharedSignOut     = () => axios.post(`${API_BASE_URL}/api/v1/auth/logout`, null, { withCredentials: true, timeout: 8000 });

// ---- Items ----
export const getItems = (params) => api.get('/items', { params });
export const getItemByQR = (qrCodeId) => api.get(`/items/scan/${qrCodeId}`);
export const getItemById = (id) => api.get(`/items/${id}`);
export const createItem = (data) => api.post('/items', data);
export const updateItem = (id, data) => api.patch(`/items/${id}`, data);
export const adminReassignItem = (id, data) => api.patch(`/items/${id}/reassign`, data);

// ---- Transactions ----
export const createTransaction = (data) => api.post('/transactions', data);
export const bulkLoadTruck = (data) => api.post('/transactions/bulk-load', data);
export const getTransactions = (params) => api.get('/transactions', { params });

// ---- Locations ----
export const getLocations    = (params) => api.get('/locations', { params });
// Jobsites near the phone + the person's Labor Board jobs this week (10/29)
export const getLocationSuggestions = (params) => api.get('/location-suggestions', { params });
export const getLocationById = (id)     => api.get(`/locations/${id}`);
export const createLocation  = (data)   => api.post('/locations', data);

// ---- Workers ----
export const getWorkers = (params) => api.get('/workers', { params });
export const getWorkerInventory = (id) => api.get(`/workers/${id}/inventory`);
// Power User sign-in popups: who the Procore sync removed / added / returned
export const getDirectoryChanges = ()              => api.get('/workers/directory-changes');
export const ackDirectoryChanges = ()              => api.post('/workers/directory-changes/ack');
export const setWorkerAtRole     = (id, at_role)   => api.patch(`/workers/${id}/at-role`, { at_role });
// Admin > Users: { at_role?, lb_role?, phone?, email?, is_active? }
export const updateWorker        = (id, data)      => api.patch(`/workers/${id}`, data);

// ---- Categories ----
export const getCategories = (params) => api.get('/categories', { params });

// ---- Suggestions ----
export const getSuggestions = (data) => api.post('/suggestions/scan', data);
export const dismissSuggestion = (data) => api.post('/suggestions/dismiss', data);

// ---- Reports ----
export const getDashboard = () => api.get('/reports/dashboard');
export const getOverdue = () => api.get('/reports/overdue');
export const getLowStock = () => api.get('/reports/low-stock');
export const getProjectInventory = (projectId) => api.get(`/reports/project/${projectId}`);

// ---- Procore ----
export const triggerProcoreSync      = ()           => api.post('/procore/sync');
export const getProcoreSyncStatus    = ()           => api.get('/procore/sync/status');
export const getProcoreSyncConflicts = ()           => api.get('/procore/sync/conflicts');
export const resolveSyncConflict     = (id, action) => api.patch(`/procore/sync/conflicts/${id}`, { action });

// ---- Health ----
export const healthCheck = () => axios.get(`${API_BASE_URL}/health`);

// ---- Delete ----
export const deleteItem = (id) => api.delete(`/items/${id}`);

// ---- Tool Requests ----
export const createToolRequest  = (data) => api.post('/requests', data);
export const getToolRequests    = (params) => api.get('/requests', { params });
export const getMyToolRequests  = () => api.get('/requests/mine');
export const updateToolRequest  = (id, data) => api.patch(`/requests/${id}`, data);

// ---- Pending Transfers ----
export const initiatePendingTransfer = (data) => api.post('/transfers', data);
export const getMyPendingTransfers   = ()      => api.get('/transfers/pending');
// Scan Multiple: several tools to one person (+ jobsite), one text (10/29)
export const initiateBatchTransfer   = (data)  => api.post('/transfers/batch', data);
// Incoming screen: accept / reject one or more of my incoming transfers
export const respondToTransfers      = (data)  => api.post('/transfers/respond', data);

// Public — no token needed (called from AcceptTransferPage with plain axios)
const publicApi = axios.create({ baseURL: `${API_BASE_URL}/api/v1`, timeout: 15000 });
export const getTransferByToken    = (token)          => publicApi.get(`/transfers/accept/${token}`);
export const respondToTransfer     = (token, action)  => publicApi.post(`/transfers/accept/${token}`, { action });

export default api;